'use client'
import { buildImageSrc } from '@/app/[locale]/(home)/_components/CategoriesList/utils'
import { useCustomLocale } from '@/_hooks/useCustomLocale'
import { ISingleOrder } from '@/_service/types/orders'
import { getStatus, getStatusName } from '@/_utils/getStatus'
import {
  Box,
  Flex,
  HStack,
  Image,
  Text,
  VStack,
} from '@chakra-ui/react'
import { format } from 'date-fns'
import { enUS, ru, uz } from 'date-fns/locale'
import { useTranslations } from 'next-intl'
import { useQueryState } from 'nuqs'
import { useMemo } from 'react'
import { getColor } from '../_components/Preview/utils/get-color-status'
import { BRAND_LOGO } from '@/_utils/Constants'
import { getCurrency } from '@/_utils/get.store'

const localeMap = {
  ru,
  en: enUS,
  uz,
}

type Props = {
  order: ISingleOrder
  onClick: (order: ISingleOrder) => void
}

const OrderCard: React.FC<Props> = ({ order, onClick }) => {
  const t = useTranslations()
  const locale = useCustomLocale()

  const [brandId] = useQueryState('id')

  const isCashback = order?.payment?.find(
    (el) => el.payment_type === 'cashback',
  )

  const total = useMemo(() => {
    if (order) {
      return isCashback
        ? order?.delivery_price + order?.order_amount - isCashback?.paid_amount
        : (order?.is_delivery_free ? 0 : order?.delivery_price) +
            order?.order_amount || 0
    } else {
      return 0
    }
  }, [order])

  return (
    <VStack
      alignItems='flex-start'
      py={{ base: '4', lg: '5' }}
      px={{ base: '3', lg: '4' }}
      rounded='20px'
      gap='10px'
      bg='background.card'
      border='1px solid'
      key={order.id}
      borderColor={{
        base: 'transparent',
        lg: brandId === order.id ? 'primary' : 'transparent',
      }}
      cursor='pointer'
      onClick={() => onClick(order)}
      transition='all 0.3s ease'
    >
      <Flex w='full' justifyContent='space-between'>
        <VStack alignItems='flex-start' gap='1'>
          <Text
            as='p'
            fontSize={{ base: '18px', lg: '20px' }}
            fontWeight='bold'
            lineHeight='120%'
            color='text'
          >
            {order?.shipper?.name}
          </Text>
          <Text
            fontSize={{ base: '14px', lg: '15px' }}
            lineHeight='120%'
            fontWeight='medium'
            color='#667085'
          >
            {/* {order?.created_at ? format(order?.created_at, 'dd MMM HH:mm') : ''} */}
            {order?.created_at
              ? format(new Date(order.created_at), 'dd MMM HH:mm', {
                  locale: localeMap[locale as keyof typeof localeMap],
                })
              : ''}
          </Text>
        </VStack>

        <VStack gap='0' alignItems='flex-end'>
          <Text
            fontSize={{ base: '16px', lg: '18px' }}
            lineHeight='120%'
            fontWeight='bold'
            color='text'
            letterSpacing='1%'
          >
            {Intl.NumberFormat('ru-RU').format(total || 0)} {getCurrency()}
          </Text>

          <Text
            textAlign='end'
            fontSize={{ base: '13px', lg: '15px' }}
            lineHeight='22px'
            fontWeight='medium'
            color={order.status_id ? getColor(getStatus(order.status_id)) : ''}
          >
            {order?.delivery_type === 'hall' && order?.pos_id
              ? t(`order_list.vendor-accepted`)
              : order?.status_id
                ? t(`order_list.${getStatusName(order?.status_id)}`)
                : ''}
          </Text>
        </VStack>
      </Flex>

      <HStack gap='1'>
        {order?.products?.slice(0, 6).map((product) => {
          const image = buildImageSrc(product?.image)

          return (
            <Box
              w={{ base: '38px', lg: '48px' }}
              h={{ base: '38px', lg: '48px' }}
              bg='gray.200'
              rounded='lg'
              overflow='hidden'
              key={product.id}
            >
              {image ? (
                <Image
                  src={image}
                  alt='product'
                  objectFit='cover'
                  height='full'
                  width='full'
                />
              ) : (
                <Image src={BRAND_LOGO[order?.shipper_id]} alt='product' />
              )}
            </Box>
          )
        })}
      </HStack>
    </VStack>
  )
}

export default OrderCard
